import type { ClientApprovalStatus } from '@peles/shared';
import { ThumbsUp, ThumbsDown, RotateCcw, Wallet } from 'lucide-react';
import type { ClientExpenseItem } from '../../api/portal';
import { DocumentCountButton } from '../documents/DocumentCountButton';
import { StatusPill } from '../common';

const APPROVAL_BADGE: Record<ClientApprovalStatus, { label: string; className: string }> = {
  PENDING: { label: 'Pending', className: 'bg-gray-100 text-gray-700' },
  APPROVED: { label: 'Approved', className: 'bg-green-100 text-green-700' },
  REJECTED: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
};

export interface ClientExpenseTableProps {
  items: ClientExpenseItem[];
  /** Shown instead of the table when there is nothing to list. */
  emptyMessage?: string;
  /** Renders the approval badge + approve/reject/reset actions. */
  onApprovalChange?: (item: ClientExpenseItem, status: ClientApprovalStatus) => void;
  approvalPending?: boolean;
  /** Renders the attached-files button on rows that carry documents. */
  onPreviewDocuments?: (item: ClientExpenseItem) => void;
  /** Renders a Pay button on rows whose payee has payment details on file. */
  onPay?: (item: ClientExpenseItem) => void;
}

const money = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

/** A null amount means the event's budget is only partly visible to this client. */
const amount = (n: number | null) => (n == null ? '-' : money(n));

/**
 * What the client still has to provide for this expense. When the producer
 * fronted it, that money goes back to the producer rather than to the vendor —
 * the client cannot tell those apart from the amount alone, so the row says so.
 */
function OpenBalance({ item }: { item: ClientExpenseItem }) {
  if (item.openBalance == null) return <span className="text-gray-400">-</span>;

  const settled = item.openBalance <= 0.005;
  return (
    <span className={settled ? 'text-green-600' : 'text-red-600 font-medium'}>
      {money(item.openBalance)}
      {!settled && item.owedToProducer && (
        <span className="block text-[10px] font-normal text-amber-600">reimburse producer</span>
      )}
    </span>
  );
}

/**
 * The client's read-only view of an event's expenses: what each line costs,
 * what has been paid toward it and what is still open. Shared by the portal
 * Expenses tab and the dashboard's vendor-selection panel so both always show
 * the same money, with each caller adding only its own row actions.
 */
export function ClientExpenseTable({
  items,
  emptyMessage = 'No expense items available.',
  onApprovalChange,
  approvalPending,
  onPreviewDocuments,
  onPay,
}: ClientExpenseTableProps) {
  if (items.length === 0) {
    return <p className="text-gray-500 text-center py-8">{emptyMessage}</p>;
  }

  const showApproval = Boolean(onApprovalChange);
  const showActions = showApproval || Boolean(onPreviewDocuments) || Boolean(onPay);

  return (
    <div className="bg-white rounded-lg border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {/* Category and vendor move under the item name on phones. */}
            <th className="hidden md:table-cell text-left p-3">Category</th>
            <th className="text-left p-3">Item</th>
            <th className="hidden md:table-cell text-left p-3">Vendor</th>
            <th className="text-right p-3">Price</th>
            <th className="text-right p-3">Paid</th>
            <th className="text-right p-3">Open Balance</th>
            <th className="hidden sm:table-cell text-left p-3">Status</th>
            {showApproval && <th className="text-left p-3">Approval</th>}
            {showActions && <th className="p-3 w-10" />}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-t" data-testid={`client-expense-${item.id}`}>
              <td className="hidden md:table-cell p-3 text-gray-600">
                {item.vendorCategoryName || '-'}
              </td>
              <td className="p-3 font-medium">
                {item.itemName || '-'}
                <span className="block md:hidden text-xs font-normal text-gray-500">
                  {[item.vendorCategoryName, item.vendorName].filter(Boolean).join(' · ')}
                </span>
              </td>
              <td className="hidden md:table-cell p-3 text-gray-600">{item.vendorName || '-'}</td>
              <td className="p-3 text-right tabular-nums">{amount(item.price)}</td>
              <td className="p-3 text-right tabular-nums text-green-600">{amount(item.paid)}</td>
              <td className="p-3 text-right tabular-nums">
                <OpenBalance item={item} />
              </td>
              <td className="hidden sm:table-cell p-3">
                {item.statusName && <StatusPill label={item.statusName} color={item.statusColor} />}
              </td>
              {showApproval && (
                <td className="p-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${APPROVAL_BADGE[item.clientApproval].className}`}
                  >
                    {APPROVAL_BADGE[item.clientApproval].label}
                  </span>
                </td>
              )}
              {showActions && (
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    {onApprovalChange && (
                      <>
                        <button
                          onClick={() => onApprovalChange(item, 'APPROVED')}
                          disabled={approvalPending}
                          className="p-1 text-gray-400 hover:text-green-600"
                          title="Approve"
                          aria-label="Approve"
                        >
                          <ThumbsUp size={14} />
                        </button>
                        <button
                          onClick={() => onApprovalChange(item, 'REJECTED')}
                          disabled={approvalPending}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Reject"
                          aria-label="Reject"
                        >
                          <ThumbsDown size={14} />
                        </button>
                        {item.clientApproval !== 'PENDING' && (
                          <button
                            onClick={() => onApprovalChange(item, 'PENDING')}
                            disabled={approvalPending}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="Reset to Pending"
                            aria-label="Reset to Pending"
                          >
                            <RotateCcw size={14} />
                          </button>
                        )}
                      </>
                    )}
                    {onPay && item.paymentMethodCount > 0 && (
                      <button
                        type="button"
                        onClick={() => onPay(item)}
                        className="min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 flex items-center justify-center text-gray-400 hover:text-primary-600 rounded-lg"
                        title="How to pay"
                        aria-label={`How to pay ${item.itemName || item.vendorName || 'this expense'}`}
                      >
                        <Wallet size={16} />
                      </button>
                    )}
                    {onPreviewDocuments && (
                      <DocumentCountButton
                        count={item.documentCount}
                        onClick={() => onPreviewDocuments(item)}
                      />
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
